import React, { useEffect, useRef, useState } from "react";
import { FiCpu, FiSend, FiTrash2, FiCopy, FiCheck } from "react-icons/fi";
import MemberPage from "../components/cowork/MemberPage";
import { apiRequest, errorMessage } from "../api";
import { cardClass } from "../components/cowork/jobUtils";

function Bubble({ message }) {
  const [copied, setCopied] = useState(false);
  const mine = message.role === "user";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available */ }
  };
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap break-words ${mine ? "bg-brand-gradient text-white rounded-br-md" : "bg-white/10 text-gray-100 rounded-bl-md"}`}>
        {message.content}
        {!mine && (
          <button onClick={copy} className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 hover:text-white" aria-label="Copy reply">
            {copied ? <><FiCheck size={11} /> Copied</> : <><FiCopy size={11} /> Copy</>}
          </button>
        )}
      </div>
    </div>
  );
}

function Assistant({ user }) {
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    apiRequest("/core/cowork/ai/", { auth: true }).then(({ ok, data }) => {
      if (!ok) return setError(errorMessage(data));
      setConfig(data);
      setMessages(data.messages);
    });
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, busy]);

  const send = async (content) => {
    const body = (content ?? text).trim();
    if (!body || busy) return;
    setBusy(true);
    setError("");
    setText("");
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: body }]);
    const { ok, data } = await apiRequest("/core/cowork/ai/", { method: "POST", auth: true, body: { message: body } });
    setBusy(false);
    if (!ok) {
      setMessages((prev) => prev.slice(0, -1));
      setText(body);
      return setError(errorMessage(data));
    }
    setMessages((prev) => [...prev.slice(0, -1), data.user, data.assistant]);
    setConfig((c) => ({ ...c, provider: data.provider }));
  };

  const clear = async () => {
    if (!window.confirm("Clear this conversation?")) return;
    await apiRequest("/core/cowork/ai/", { method: "DELETE", auth: true });
    setMessages([]);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!config) return error ? <p className="text-red-400">{error}</p> : <p className="text-gray-400">Loading...</p>;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-accent/15 text-accent-light flex items-center justify-center"><FiCpu size={22} /></span>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight leading-tight">{config.name}</h1>
            <p className="text-xs text-gray-400">
              {config.provider === "claude" ? "Powered by Claude" : "Basic assistant mode - ask the site owner to connect an AI key for open-ended answers"}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button onClick={clear} className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5"><FiTrash2 size={14} /> Clear chat</button>
        )}
      </div>

      {!config.enabled ? (
        <div className={`${cardClass} p-10 text-center`}>
          <p className="text-lg font-semibold">The assistant is turned off right now</p>
          <p className="text-gray-400 mt-1">Please check back later.</p>
        </div>
      ) : (
        <div className={`${cardClass} flex flex-col overflow-hidden`}>
          <div className="px-5 py-5 space-y-3 overflow-y-auto min-h-[380px] max-h-[520px]">
            <div className="flex justify-start">
              <div className="max-w-[85%] px-4 py-3 rounded-2xl rounded-bl-md bg-white/10 text-sm text-gray-100">{config.welcome}</div>
            </div>
            {messages.map((m) => <Bubble key={m.id} message={m} />)}
            {busy && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white/10 text-sm text-gray-400 inline-flex gap-1" aria-label="Thinking">
                  <span className="animate-pulse">●</span><span className="animate-pulse [animation-delay:150ms]">●</span><span className="animate-pulse [animation-delay:300ms]">●</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length === 0 && (
            <div className="px-5 pb-3 flex flex-wrap gap-2">
              {config.suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} className="px-3.5 py-1.5 rounded-full bg-accent/10 border border-accent-light/30 text-accent-light text-sm hover:bg-accent/20">{s}</button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-navy-border p-3 flex items-end gap-2">
            <textarea
              rows={1}
              value={text}
              maxLength={2000}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={user.role === "buyer" ? "Ask about job descriptions, budgets, choosing a seller..." : "Ask for a cover letter, rate advice, profile tips..."}
              className="flex-1 resize-none px-4 py-2.5 rounded-xl bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light max-h-32"
            />
            <button disabled={!text.trim() || busy} className="w-11 h-11 shrink-0 rounded-full bg-brand-gradient flex items-center justify-center disabled:opacity-40" aria-label="Send"><FiSend /></button>
          </form>
          {error && <p className="px-5 pb-3 text-sm text-red-400">{error}</p>}
        </div>
      )}
      <p className="text-xs text-gray-500 mt-3">Your chat with the assistant is private to you. Don't share passwords or payment details.</p>
    </>
  );
}

export default function CoWorkAssistantPage() {
  return <MemberPage title="AI Assistant">{({ user }) => <Assistant user={user} />}</MemberPage>;
}
